
import { Component, Mixins, Watch } from 'vue-property-decorator';
import Page from '@/views/Page.vue';
import BAEventShare from '@/views/admin/BAEventShare.vue'
import { AuthMixin, BAIconsMixin, FormMixin, StripeMixin, AthleteRoutingMixin, QRCodeMixin, PaginatedTableMixin, AthletesCacheMixin, StylesMixin } from '@/mixins';
import { BAEventModel, EventParticipantDetails, EventTicketModel } from '@/models/baEvent';
import { BAEventAdminDetail } from '@/../types/constants/admin.routes'
import { DataTableHeader } from 'vuetify';
import { userStore, sportStore } from '@/store';
import OnboardingDialog from '@/views/onboarding/OnboardingDialog.vue';
import BestAthletesEventPurchaseDialog from '@/components/events/BestAthletesEventPurchaseDialog.vue';
import BAEventParticipants from '@/components/events/BAEventParticipants.vue';
import { athleteApi } from '@/api/AthleteApi';
import { baEventApi } from '@/api/BAEventsApi';
import { baPurchaseApi } from '@/api/BAPurchaseApi';
import { AgreementFile } from '@/models/file/FileModel';
import { BAPurchaseModel, AthleteProfileModel } from '@/models';
import { BAEventStatus } from '@/models/baEvent/BAEventModel';
import { RepositoryQuery, SportName, PurchaseStep } from '@/../types';
import { getTimeZoneAbbreviation } from '@/helpers/date';

@Component({
	components: {
		Page,
        BAEventShare,
        OnboardingDialog,
        BestAthletesEventPurchaseDialog,
        BAEventParticipants,
	}
})
export default class BAEventDetailPage extends Mixins(StylesMixin, AuthMixin, BAIconsMixin, FormMixin, StripeMixin, AthleteRoutingMixin, QRCodeMixin, PaginatedTableMixin, AthletesCacheMixin) {
    eventLoaded: boolean = false;
    eventLoading: boolean = false;
    baEvent: BAEventModel = undefined;
    baTicket: EventTicketModel = undefined;
    baParticipant: EventParticipantDetails = undefined;
    qrCode: string = undefined;
    qrCodeUrl: string;
    showBARating: boolean = false;

    getTimeZoneAbbreviation = getTimeZoneAbbreviation;

    // types
    PurchaseStep = PurchaseStep;

    get IsLoading(): boolean {
        return this.eventLoading;
    }

    get IsPublicView(): boolean {
        return this.$route.path.includes('public');
    }

    beforeRouteUpdate(to, from, next) {
    if (to.params.eventId === from.params.eventId) {
        // same eventId -> force reload anyway
        this.handleEventLoad().then(() => next());
    } else {
        // normal behavior: watcher will handle changes
        next();
    }
}

    async created() {
        const sports = await sportStore.loadSports();
        this.tableOptions.itemsPerPage = 10;
        this.localForagePersistFields = [['search', ''],['tableOptions.page', 1],['tableOptions.itemsPerPage', 10]];
        await this.handleEventLoad();
    }

	@Watch('$route.params.eventId') 
    async updateEvent() {
		await this.handleEventLoad();
	}

    get eventId(): string {
        return this.$route.params.eventId;
    }

    private async handleEventLoad() {
        if( this.IsPublicView && userStore.IsReady ) {
                this.goToEvent(this.eventId);
                return;
        } else {
            await this.loadEvent();
        }
        
    }

    async loadEvent(): Promise<BAEventModel> {
        try {
            this.eventLoaded = false;
            this.eventLoading = true;
            this.baEvent = undefined;
            this.baTicket = undefined;
            this.baParticipant = undefined;
            this.associatedEvents = undefined;
            this.futureAssociatedEvents = undefined;
            this.purchases = undefined;
            this.hasFutureAssociatedEvents = false;
            this.prerequisite = undefined;
            this.hasPrerequisite = false;

            this.baEvent = await baEventApi.findById(this.eventId);
            if( this.baEvent.status === BAEventStatus.deleted ) this.baEvent = undefined;
            if( this.IsNotEmpty(this.baEvent) ) {
                this.baEvent.loadEx();
                this.baTicket = await this.RetrieveTicket();
                if( this.IsNotEmpty(this.baTicket) ) {
                    if( this.baEvent.Online ) {
                        this.qrCode = await this.generateEventQRCode(this.baEvent.meetingUrl, this.baEvent?.id);
                    } else {
                        this.qrCode = await this.generateTicketQRCode(this.baEvent?.id, this.baTicket?.id, this.AthleteId);
                    }
                    const participant = this.baEvent.participants.find(p => p.attendeeId === this.AthleteId);
                    if( participant ) {
                        this.baParticipant = new EventParticipantDetails().load(participant);
                        this.baParticipant.athlete = await athleteApi.findById(participant.attendeeId);
                        this.baParticipant.ticket = await this.baEvent.Ticket(participant.ticketId);
                    } else {
                        this.baParticipant = new EventParticipantDetails().load({
                            attendeeId: this.AthleteId,
                            ticketId: this.baTicket.id,
                            athlete: await athleteApi.findById(this.AthleteId),
                            ticket: this.baTicket
                        });
                    }
                }

                if( this.IsNotEmpty( this.baEvent.prerequisite ) ) this.prerequisite = await baEventApi.findById(this.baEvent.prerequisite);
                if( this.IsNotEmpty( this.AthleteId ) ) {
                    this.purchases = await baPurchaseApi.findByAthleteId( this.AthleteId );
                    if( this.IsNotEmpty(this.purchases) ) this.prerequisitePurchase = this.purchases.find(p => p.productId === this.baEvent.prerequisite );
                }
                this.hasPrerequisite = this.IsNotEmpty(this.prerequisite) && this.IsEmpty(this.prerequisitePurchase);
                
                // Obtain associated events
                const query: RepositoryQuery<BAEventModel> = {$match: {prerequisite: this.baEvent.id}};
                const response = await baEventApi.queryAll(query);
                this.associatedEvents = response.docs.filter(e => !e.invitational);

                if( this.IsNotEmpty(this.associatedEvents) ) {
                    this.futureAssociatedEvents = this.associatedEvents.filter(e => e.IsFuture);
                    this.hasFutureAssociatedEvents = this.IsNotEmpty(this.futureAssociatedEvents);
                }
            }

            this.viewParticipants = false;
            this.showBARating = this.IsAdmin && ( this.baEvent.sportName === SportName.Soccer );
            if( this.AthleteProfile && this.IsNotEmpty(this.AthleteProfile.nextUrl)) this.eventInterest = this.AthleteProfile.nextUrl.includes(this.baEvent.id);

            this.eventLoaded = true;
            this.eventLoading = false;
        } catch(e) {
            console.error(`Error loading BA event: `, e);
            return undefined;
        }
    }

    get TicketHeaders(): DataTableHeader<any>[] {
        return [
            {text: 'Name', value: 'name'},
            {text: 'Description', value: 'description'},
			{text: 'Price', width:"150", value: 'price'},
            {text: '', width:"50", value: 'actions', sortable: false},
            {text: '', value: 'data-table-expand'},
        ]
    }

    couponCode: string = '';
    showCouponCode: boolean = false;
    get CouponCode(): string {
        return this.couponCode.trim()
    }

    purchase: BAPurchaseModel;
    async RetrieveTicket(): Promise<EventTicketModel> {
        if( this.IsEmpty(this.baEvent) ) return undefined;
        if( !this.AthleteAppActive ) return undefined;

        this.purchase = await this.PurchaseRetrieve(undefined, this.AthleteId, this.baEvent.id);
        if( this.IsEmpty(this.purchase) ) return undefined;

        if( !this.purchase?.completed ) {
            console.warn(`WARNING: Purchase not logged as completed`);
        };

        const ticket = await this.baEvent.tickets.find(t => t.id === this.purchase?.priceId);
        return ticket;
    }

    eventInterest: boolean = false;
    get AthleteProfile(): AthleteProfileModel {
        if( !this.AthleteAppActive ) return undefined;
        if( !userStore.athlete ) return undefined;

        return userStore.athlete;
    }
	get AthleteId(): string {
		if( !this.AthleteAppActive) return undefined;
        if( !userStore.athleteId ) return undefined;

        return userStore.athleteId;
    }

	async generateTicketQRCode(eventId: string, ticketId: string, userId: string): Promise<string> {
        this.qrCodeUrl = `${this.AdminLInk}/baevent-redeem?userId=${userId}&eventId=${eventId}&ticketId=${ticketId}`;
        return await this.generateQRCode(this.qrCodeUrl);
    }
    async generateEventQRCode(meetingUrl: string, eventId: string): Promise<string> {
        this.qrCodeUrl = this.IsNotEmpty(meetingUrl)? `${meetingUrl}/?eventId=${eventId}` : `${this.AthleteAppUrl}/baevent/${eventId}`;
        return await this.generateQRCode(this.qrCodeUrl);
    }

    onEditEvent() {
		this.$router.push({
			name: BAEventAdminDetail,
			params: { eventId: this.eventId }			
		});
    }

    onDownloadEventFile() {
        if( this.IsEmpty(this.baEvent.fileUrl) ) return;
        const link = document.createElement('a');
        link.href = this.baEvent.fileUrl;
        link.download = `${this.baEvent?.name}.pdf`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    agreementUnderReview: AgreementFile;
    agreementReview: boolean = false;
    async onReviewAgreement(agreement) {
        this.agreementUnderReview = agreement;
        await this.agreementUnderReview.read();
        this.agreementReview = true;
    }
    onCloseReview() {
        this.agreementReview = false;
    }


    get ShowAthleteInformation(): boolean {
        if( this.IsEmpty(this.baEvent) ) return false;
        return this.AthleteAppActive || this.IsPublicView;
    }
    get ShowCoachInformation(): boolean {
        if( this.IsEmpty(this.baEvent) ) return false;
        return this.CoachAppActive && this.baEvent.coaches.includes(this.CoachId);
    }

    viewParticipants: boolean = false;
    async onParticipantsExpand() {
        this.viewParticipants = true;
        await this.loadTable();
    }
    async onParticipantsCollapse() {
        this.viewParticipants = false;
    }

    participants: Array<EventParticipantDetails> = [];
    searchParticipants: string = "";
    async updateParticipantsSearch(search: string) {
        this.searchParticipants = search;
        this.updateSearch();
    }
	get TotalItems(): number {
        return this.baEvent.participants.length;
	}

    loadStatus: string = "";
    async loadParticipants() {
        if( this.IsEmpty(this.baEvent) ) return;
        if( !this.viewParticipants ) return;
        this.dataItems = [];

        this.loadStatus = "Loading Participants...";
        // filter participants
        if( this.IsNotEmpty(this.searchParticipants) && this.IsNotEmpty(this.participants) ) {
            const filteredParticipants: Array<EventParticipantDetails> = this.participants.filter(p => (this.IsNotEmpty(p.tags) && p.tags.includes(this.searchParticipants.toLowerCase().trim())));
            this.dataItems = filteredParticipants;
            this.dataItemsCount = filteredParticipants.length;
            return;
        }

        const participantsToDisplay = this.baEvent.participants.slice(this.FirstTableItemIndex, this.LastTableItemIndex);
        this.loadStatus = `Loading ${participantsToDisplay.length} Participants...`;
        const completeParticipants: Array<EventParticipantDetails> = await Promise.all( participantsToDisplay.map(async(attendee) => {
            const attendeeDetails: EventParticipantDetails = new EventParticipantDetails().load(attendee);
            attendeeDetails.athlete = await this.AthleteRetrieve(attendee.attendeeId);
            attendeeDetails.purchase = await this.PurchaseRetrieve(attendee.purchaseId, attendee.attendeeId, this.baEvent.id);
            if( this.IsEmpty(attendee.purchaseId) ) attendee.purchaseId = attendeeDetails.purchase?.id;
            attendeeDetails.ticket = await this.baEvent.Ticket(attendee.ticketId);
            attendeeDetails.id = attendeeDetails.athlete?.id;
            if( this.showBARating ) await attendeeDetails.fetchBARating();
            else attendeeDetails.recruitingProfile = await this.RecruitingProfileRetrieve(attendeeDetails.id);
            attendeeDetails.data = await this.AthleteDataRetrieve(attendeeDetails.id, attendeeDetails.Data);
            attendeeDetails.tags = `${attendeeDetails.FullName} ${attendeeDetails.Email} ${attendeeDetails.Organization} ${attendeeDetails.TeamName} ${attendeeDetails.id}`.toLowerCase();
            
            return attendeeDetails;
        }));

        if( this.showBARating ) {
            this.participants = completeParticipants.sort((a,b) => (b.baRating?.rating - a.baRating?.rating) )
        } else {
            this.participants = completeParticipants.sort((a,b) => {
                if( this.IsEmpty(a?.athlete?.lastName) && this.IsEmpty(b?.athlete?.lastName) ) return 0;
                if( this.IsEmpty(b?.athlete?.lastName) ) return 1;
                if( this.IsEmpty(a?.athlete?.lastName) ) return -1;
                if( a.athlete.lastName.toLowerCase() > b.athlete.lastName.toLowerCase() ) return 1;
                if( a.athlete.lastName.toLowerCase() < b.athlete.lastName.toLowerCase() ) return -1;
                return 0;
            })
        }

        this.dataItems = this.participants;
        this.dataItemsCount = this.participants.length;
        this.loadStatus = "";
    }
    async loadTable() {
        this.isLoading = true;
        await this.loadParticipants();
        this.isLoading = false;
    }

    isPurchaseDataChanged: boolean = false;
    isPurchaseDataSaving: boolean = false;
    onChangePurchaseData() {
        this.isPurchaseDataChanged = true;
    }
    async onConfirmPurchaseData() {
        this.isPurchaseDataSaving = true;
        await baPurchaseApi.patch({
            id: this.purchase.id,
            data: this.purchase.data,
        })

        this.isPurchaseDataChanged = false;
        this.isPurchaseDataSaving = false;
    }

    prerequisite: BAEventModel;
    prerequisitePurchase: BAPurchaseModel;
    hasPrerequisite: boolean = false;
    async gotoPrerequisite() {
        if( this.AthleteAppActive ) {
            const athleteProfile = await athleteApi.findById(this.AthleteId);
            if( this.IsNotEmpty(athleteProfile) && this.IsEmpty(athleteProfile.nextUrl) ) {
                const nextUrl=`/${this.baEvent.ShareLink}`
                await athleteApi.patch({id: this.AthleteId, nextUrl});
            }
            this.goToEvent(this.prerequisite?.id);
        }
        else if( this.IsPublicView) {
            this.gotoAthletePublicLink(this.prerequisite.ShareLink, '_self');
        }
    }
    async onRemoveNextUrl() {
        if( !this.AthleteAppActive ) return;
        const nextUrl = null;
        await athleteApi.patch({id: this.AthleteId, nextUrl});
        this.gotoHome();
    }

    purchases: Array<BAPurchaseModel>;
    associatedEvents: Array<BAEventModel>;
    futureAssociatedEvents: Array<BAEventModel>;
    hasFutureAssociatedEvents: boolean = false;
    HasPurchase(event: BAEventModel): boolean {
        if( this.IsEmpty(this.purchases) ) return false;

        const purchaseOfEvent = this.purchases.find(p => p.productId === event.id);
        return this.IsNotEmpty(purchaseOfEvent);
    }
    
    // Export Data
    onParticipantsExport() {
        const now: Date = new Date();
        this.exportCSVFromParticipants(`event-participants-${this.baEvent?.name}-${this.formatDateFilename(now)}.csv`);
    }

    async exportCSVFromParticipants(filename: string): Promise<void> {
        // create header labels composed of all the 'data' participants have filled out
        const dataLabels = new Set<string>();
        this.baEvent.dataLabels.forEach(label => dataLabels.add(label));
        this.participants.forEach(p => {
            const participantKeys = Object.keys(p.Data());
            participantKeys.forEach(key => {
                const existingLabels = Array.from(dataLabels);
                const existingKey = existingLabels.find(l => this.NormalizeString(l) === this.NormalizeString(key));
                if( this.IsEmpty(existingKey) ) dataLabels.add(key)
            });
        })
        
        const headerLabels = Array.from(dataLabels);
        const headers = [...headerLabels,'Name','Profile Link','Team','Organization','Mindset Archetype','Registration Date'];
        const rows = this.participants.map(p => {
            const pData = p.Data(this.baEvent.dataLabels, p.purchase?.data);
            const participantKeys = Object.keys(pData);
            // obtain data for each header label
            const participantData = headerLabels.map(h => {
                const key = participantKeys.find(k => this.NormalizeString(k) === this.NormalizeString(h));
                if( this.IsNotEmpty(key) ) return this.escapeCSV(pData[key]);
                return " - ";
            })
            // add 'Name','Profile Link','Mindset Archetype'
            const hasMindset = this.IsNotEmpty(p.recruitingProfile?.mindsetReport) && !p.recruitingProfile?.mindsetReport.HasError;
            participantData.push(...[
                this.escapeCSV(p.FullName),
                this.escapeCSV(`${this.PublicLink}${p.athlete?.PublicSharingUrl}`),
                this.escapeCSV(p.TeamName),
                this.escapeCSV(p.Organization),
                this.escapeCSV(hasMindset? p.recruitingProfile?.mindsetReport.archetype : ''),
                this.escapeCSV(p.purchase? p.purchase.date : ''),
            ]);
            return participantData;
        })

        const csvContent = [headers.join(","), ...rows.map(row => row.join(","))].join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
    }

    onNoEventCode() {
        this.showCouponCode = false
        this.couponCode = '';
    }

	isRemovingReminder: boolean = false;
	async onRemoveIncompletePurchase(purchase: BAPurchaseModel) {
		if( this.IsEmpty(this.AthleteProfile) ) return;

		this.isRemovingReminder = true;
		await baPurchaseApi.delete(purchase);
		await athleteApi.patch({id: this.AthleteProfile.id, nextUrl: null})
		this.AthleteProfile.nextUrl = null;
        this.baTicket = undefined;
		this.isRemovingReminder = false;
	}

}
